import React, { useMemo } from 'react';
import { Card, DatePicker, Divider, Select, Space, Tooltip } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FilterBar } from '../../../components/list';
import { useUserSegmentScope } from '../../auth/hooks/useUserSegmentScope';
import { usePermission } from '../../permissions/PermissionsContext';
import { useCustomerSegmentOptions } from '../../../hooks/options/useCustomerSegmentOptions';
import { useContactCategories } from '../../contacts/hooks/useContactCategories';
import { useUserOptions } from '../../../hooks/options/useUserOptions';

dayjs.extend(utc);

const { RangePicker } = DatePicker;

export function defaultTaskDashboardMonthRange(): [Dayjs, Dayjs] {
  const start = dayjs().utc().startOf('month');
  return [start, start.add(1, 'month')];
}

export interface TaskDashboardFilterState {
  /** Null = all-time (no fromUtc/toUtc). Otherwise [inclusive start, exclusive end). */
  dateRange: [Dayjs, Dayjs] | null;
  segmentIds: string[];
  contactCategoryIds: number[];
  assigneeIds: string[];
}

interface Props {
  value: TaskDashboardFilterState;
  onChange: (value: TaskDashboardFilterState) => void;
  /** Placed at the end of the filter row (e.g. Open tasks, Refresh). */
  trailingActions?: React.ReactNode;
  /** Optional note under the filter controls (e.g. stale placeholder data). */
  staleNote?: React.ReactNode;
}

const TaskDashboardFilterBar: React.FC<Props> = ({
  value,
  onChange,
  trailingActions,
  staleNote,
}) => {
  const { isScoped, assignedSegmentIds } = useUserSegmentScope();
  const { data: segments = [] } = useCustomerSegmentOptions({ activeOnly: true });
  const { data: categoriesData } = useContactCategories();
  const { data: users = [] } = useUserOptions();
  const canManage = usePermission('administrator.access');

  const segmentOptions = useMemo(() => {
    const all = segments.map((s) => ({ value: s.id, label: s.label }));
    if (!isScoped) {
      return all;
    }
    const allowed = new Set(assignedSegmentIds);
    return all.filter((o) => allowed.has(o.value));
  }, [segments, isScoped, assignedSegmentIds]);

  const categoryOptions = useMemo(
    () =>
      (categoriesData ?? [])
        .filter((c) => c.isActive)
        .map((c) => ({ value: c.id, label: c.label })),
    [categoriesData]
  );

  const assigneeOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        label: u.label,
      })),
    [users]
  );

  const defaultRange = defaultTaskDashboardMonthRange();
  const hasFilters =
    value.segmentIds.length > 0 ||
    value.contactCategoryIds.length > 0 ||
    value.assigneeIds.length > 0 ||
    value.dateRange === null ||
    (value.dateRange !== null &&
      (!value.dateRange[0].isSame(defaultRange[0], 'day') ||
        !value.dateRange[1].isSame(defaultRange[1], 'day')));

  return (
    <Card size="small" styles={{ body: { paddingTop: 12, paddingBottom: 12 } }}>
      <FilterBar
        onReset={
          hasFilters
            ? () =>
                onChange({
                  dateRange: defaultTaskDashboardMonthRange(),
                  segmentIds: [],
                  contactCategoryIds: [],
                  assigneeIds: [],
                })
            : undefined
        }
      >
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Date Range</span>
            <Space wrap size={12} align="center">
              <Tooltip title="Clear the range for all-time KPIs. Execution columns still use today.">
                <span style={{ display: 'inline-block' }}>
                  <RangePicker
                    allowClear
                    value={value.dateRange ?? undefined}
                    onChange={(dates) => {
                      if (!dates?.[0] || !dates[1]) {
                        onChange({ ...value, dateRange: null });
                      } else {
                        onChange({ ...value, dateRange: [dates[0], dates[1]] });
                      }
                    }}
                  />
                </span>
              </Tooltip>
              <Divider type="vertical" style={{ height: 32, margin: 0 }} />
            </Space>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 160, flex: '1 1 140px' }}>
            <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Customer Segments</span>
            <Select<string[]>
              mode="multiple"
              allowClear
              options={segmentOptions}
              value={value.segmentIds}
              onChange={(segmentIds) => onChange({ ...value, segmentIds })}
              optionFilterProp="label"
              maxTagCount="responsive"
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 160, flex: '1 1 140px' }}>
            <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Contact Categories</span>
            <Select<number[]>
              mode="multiple"
              allowClear
              options={categoryOptions}
              value={value.contactCategoryIds}
              onChange={(contactCategoryIds) => onChange({ ...value, contactCategoryIds })}
              optionFilterProp="label"
              maxTagCount="responsive"
            />
          </div>
          {canManage ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 160, flex: '1 1 140px' }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Assignees</span>
              <Select<string[]>
                mode="multiple"
                allowClear
                options={assigneeOptions}
                value={value.assigneeIds}
                onChange={(assigneeIds) => onChange({ ...value, assigneeIds })}
                optionFilterProp="label"
                maxTagCount="responsive"
              />
            </div>
          ) : null}
          {trailingActions ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto' }}>
              {trailingActions}
            </div>
          ) : null}
        </>
      </FilterBar>
      {staleNote && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--ant-color-border-secondary, #f0f0f0)' }}>
          {staleNote}
        </div>
      )}
    </Card>
  );
};

export default TaskDashboardFilterBar;
